import { calculateDistanceBetweenCoordinates } from 'shared/map/lib/utils';
import GroupColourGenerator from './color-definer';
import { COORD_PROVIDER_GPS, } from 'entities/device.map.track/model/types';
export default class GroupLogic {
    currentGroup;
    groupHighlight;
    points;
    events;
    current_event_index;
    time;
    groups;
    currStart;
    currEnd;
    currIndex;
    colourGen;
    constructor() {
        this.currentGroup = null;
        this.groupHighlight = null;
        this.groups = [];
        this.currEnd = -1;
        this.currStart = -1;
        this.currIndex = 0;
        this.colourGen = new GroupColourGenerator();
    }
    calc_groups(points, events, time) {
        this.#set_params(points, events, time);
        return this.#divide_into_groups(points);
    }
    #divide_into_groups(points) {
        points.forEach((point, index) => {
            const point_type = this.#pointType(point);
            const is_highlight_point = this.#is_highlight(point);
            if (this.#is_new_group_by_stop()) {
                this.#create_new_group_with_new_colour(is_highlight_point);
                this.#set_new_current_group_params(point_type, is_highlight_point);
            }
            else if (this.#is_new_group_by_highlight(point_type, is_highlight_point)) {
                this.#create_new_group_with_old_colour(is_highlight_point);
                this.#set_new_current_group_params(point_type, is_highlight_point);
            }
            this.currIndex = index;
        });
        this.#create_new_group_with_new_colour(this.groupHighlight ?? false);
        this.#sort_groups();
        return this.groups;
    }
    #pointType(point) {
        const point_index = this.currIndex;
        const prev = this.points[point_index - 1];
        if (point.teleport) {
            return 'teleport';
        }
        else if (point.prov !== COORD_PROVIDER_GPS) {
            return 'single_coarse';
        }
        else if (point.single) {
            if (this.#is_gps(point, prev)) {
                return 'gps';
            }
            else {
                return 'single_gps';
            }
        }
        else {
            return 'gps';
        }
    }
    #is_new_group_by_stop() {
        const current_point = this.#get_current_point();
        return this.#is_current_point_stop(current_point);
    }
    #is_new_group_by_highlight(point_type, is_highlight) {
        return point_type !== this.currentGroup || is_highlight !== this.groupHighlight;
    }
    #is_current_point_stop(point) {
        const current_nearest_stop = this.#get_nearest_stop();
        const next_point = this.#get_next_point();
        if (next_point == null || current_nearest_stop == null) {
            return false;
        }
        if (current_nearest_stop.time === undefined) {
            return false;
        }
        const time_between_event_and_point = current_nearest_stop.time - point.time.getTime() / 1e3;
        const time_between_event_and_next_point = current_nearest_stop.time - next_point.time.getTime() / 1e3;
        if (Math.abs(time_between_event_and_point) <
            Math.abs(time_between_event_and_next_point)) {
            this.current_event_index += 1;
            return true;
        }
        return false;
    }
    #create_group_from_prev_points(highlight_status, colour) {
        this.#set_group_end(highlight_status);
        if (this.#group_exist()) {
            this.#create_new_group(colour);
        }
    }
    #create_new_group_with_new_colour(highlight_status) {
        const new_colour = this.colourGen.get_new_colour();
        this.#create_group_from_prev_points(highlight_status, new_colour);
    }
    #create_new_group_with_old_colour(highlight_status) {
        const old_colour = this.colourGen.get_current_colour();
        this.#create_group_from_prev_points(highlight_status, old_colour);
    }
    #set_group_end(highlight_status) {
        this.currEnd =
            this.groupHighlight === highlight_status &&
                (this.currentGroup == 'gps' || this.currentGroup == 'single_gps')
                ? this.currIndex - 1
                : this.currIndex;
    }
    #set_new_current_group_params(point_type, is_highlight) {
        this.currentGroup = point_type;
        this.groupHighlight = is_highlight;
        this.currStart = this.currEnd;
    }
    #group_exist() {
        return (this.currStart !== -1 &&
            this.groupHighlight !== null &&
            this.currentGroup !== null);
    }
    #create_new_group(group_colour) {
        const new_group = this.#get_new_group(group_colour);
        this.#push_new_group(new_group);
    }
    #get_new_group(group_colour) {
        return {
            type: this.currentGroup ?? 'gps',
            highlight: this.groupHighlight ?? false,
            start: this.currStart,
            end: this.currEnd + 1,
            colour: group_colour,
        };
    }
    #push_new_group(group) {
        this.groups.push(group);
    }
    #is_highlight(point) {
        return (this.time == null ||
            (this.time[0] <= point.time.getTime() &&
                point.time.getTime() <= this.time[1]));
    }
    #is_gps(point, prev) {
        return (prev != null &&
            point.time.getTime() - prev.time.getTime() < 60e3 &&
            calculateDistanceBetweenCoordinates(prev.loc, point.loc) < 100);
    }
    #set_params(points, events, time) {
        this.points = points;
        this.events = [];
        this.time = time;
        events.map((event) => {
            this.events.push(event);
        });
        this.current_event_index = 0;
    }
    #sort_groups() {
        this.groups.sort(function (g1, g2) {
            if (g1.highlight < g2.highlight)
                return -1;
            if (g1.highlight > g2.highlight)
                return +1;
            return 0;
        });
    }
    #get_current_point() {
        return this.points[this.currIndex];
    }
    #get_next_point() {
        if (this.currIndex == this.points.length) {
            return null;
        }
        return this.points[this.currIndex + 1];
    }
    #get_nearest_stop() {
        // для автоскана здесь всегда возвращается null, так как там мы не красим треки
        const currEvent = this.events[this.current_event_index];
        while (this.current_event_index < this.events.length) {
            if (currEvent.time !== undefined && this.#is_stop_event(currEvent)) {
                return currEvent;
            }
            this.current_event_index++;
        }
        return null;
    }
    #is_stop_event(event) {
        return event.icon_class === 'ui-icon-map-stop';
    }
}
